// RelationQuickAddFields.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { normalizeSchemaFieldType } from 'utilities/FieldTypes';
import {
  formatPhoneValueForInput,
  isPhoneMaskableInput,
} from 'utilities/Phone';

// CSS
import './RelationQuickAddFields.css';

// Components
import Checkbox from 'components/checkbox/Checkbox';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import NodeLocationField from 'node/form/fields/location/NodeLocationField';

/**
 * Renders one lightweight quick-add input for a display field.
 * Heavier types (e.g. location) reuse their dedicated controls; others start
 * as text/select so we can deepen later without changing the modal contract.
 *
 * @param {object} field
 * @param {unknown} value
 * @param {boolean} disabled
 * @param {function} onChange
 * @returns {ReactNode}
 */
const RelationQuickAddFieldInput = ({ field, value, disabled, onChange }) => {
  const { theme } = useTheme();
  const type = normalizeSchemaFieldType(field?.type);
  const label = String(field?.label || field?.name || '').trim() || 'Field';

  if (type === 'boolean') {
    const checked = value === true;
    return (
      <div className="relation-quick-add-fields-boolean">
        <Checkbox
          color={theme.foregroundMuted}
          value={checked}
          size="24"
          label={label}
          fontSize="14px"
          onChange={disabled ? undefined : () => onChange?.(!checked)}
        />
      </div>
    );
  }

  if (type === 'enum') {
    const enumOptions = resolveEnumOptionsForField(field, 'en').map((option) => ({
      value: option.value,
      label: getOptionLabel(option, 'en') || String(option.value || ''),
      iconKey: option.iconKey || '',
      color: option.color || '',
    }));

    return (
      <SelectInput
        value={value == null ? '' : String(value)}
        options={[{ value: '', label: '' }, ...enumOptions]}
        placeholder={label}
        disabled={disabled}
        width="100%"
        onValueChange={(nextValue) => onChange?.(nextValue)}
      />
    );
  }

  if (type === 'location') {
    return (
      <NodeLocationField
        value={value || null}
        readOnly={disabled}
        onLocationSaved={(nextValue) => onChange?.(nextValue || null)}
      />
    );
  }

  if (type === 'phone') {
    const displayValue = formatPhoneValueForInput(value);
    return (
      <TextInput
        value={displayValue}
        placeholder={label}
        disabled={disabled}
        onChange={(event) => {
          if (disabled) return;
          const nextValue = event?.target?.value ?? '';
          if (!isPhoneMaskableInput(nextValue)) {
            onChange?.(nextValue);
            return;
          }
          onChange?.(formatPhoneValueForInput(nextValue));
        }}
      />
    );
  }

  const isMultiline = type === 'richtext' || type === 'text';

  return (
    <TextInput
      value={value == null ? '' : String(value)}
      placeholder={label}
      disabled={disabled}
      expandable={isMultiline}
      multiline={isMultiline}
      onChange={(event) => {
        if (disabled) return;
        onChange?.(event?.target?.value ?? '');
      }}
    />
  );
};

/**
 * RelationQuickAddFields
 *
 * Inline create form for Relation multi-select: title plus configured
 * display fields.
 *
 * @param {string} singularLabel
 * @param {string} title
 * @param {string} [titleError]
 * @param {object[]} fieldDefs
 * @param {Record<string, unknown>} values
 * @param {boolean} [disabled]
 * @param {boolean} [autoFocus]
 * @param {function} onTitleChange
 * @param {function} onFieldChange - (fieldName, nextValue) => void
 *
 * @returns {ReactNode}
 */
const RelationQuickAddFields = ({
  singularLabel,
  title,
  titleError = '',
  fieldDefs = [],
  values = {},
  disabled = false,
  autoFocus = false,
  onTitleChange,
  onFieldChange,
}) => {
  const safeFields = useMemo(
    () => (Array.isArray(fieldDefs) ? fieldDefs : []).filter((field) => String(field?.name || '').trim()),
    [fieldDefs],
  );

  const titlePlaceholder = `New ${String(singularLabel || 'item').toLowerCase()} title`;

  return (
    <div className="relation-quick-add-fields-container">

      {/* TITLE */}
      <TextInput
        value={title}
        placeholder={titlePlaceholder}
        helperText={titleError}
        error={Boolean(titleError)}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={(event) => {
          if (disabled) return;
          onTitleChange?.(event?.target?.value ?? '');
        }}
      />

      {safeFields.map((field) => {
        const name = String(field.name || '').trim();
        return (
          <div
            className="relation-quick-add-fields-field"
            key={name}
          >
            <RelationQuickAddFieldInput
              field={field}
              value={values?.[name]}
              disabled={disabled}
              onChange={(nextValue) => onFieldChange?.(name, nextValue)}
            />
          </div>
        );
      })}
    </div>
  );
};

RelationQuickAddFields.displayName = 'RelationQuickAddFields';

export default RelationQuickAddFields;
